import { create } from "zustand";
import { Conversation } from "@/api/models/api-types";

// ===== Conversations Store State =====
interface ConversationsState {
  // Data
  conversations: Conversation[];
  activeConversationId: string | null;

  // API Loading States
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: Record<string, boolean>; // Track individual deletion states
  error: Error | null;

  // Pagination
  currentPage: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  totalConversations: number;
}

// ===== Conversations Store Actions =====
interface ConversationsActions {
  // Data Management
  setConversations: (conversations: Conversation[]) => void;
  addConversations: (conversations: Conversation[]) => void; // For pagination
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  removeConversation: (id: string) => void;

  // Active Chat Management
  setActiveConversation: (id: string | null) => void;

  // Loading States
  setLoading: (loading: boolean) => void;
  setCreating: (creating: boolean) => void;
  setDeleting: (id: string, deleting: boolean) => void;
  setError: (error: Error | null) => void;

  // Pagination
  setCurrentPage: (page: number) => void;
  setHasNextPage: (hasNext: boolean) => void;
  setFetchingNextPage: (fetching: boolean) => void;
  setTotalConversations: (total: number) => void;

  // Utilities
  reset: () => void;
  getConversationById: (id: string) => Conversation | undefined;
}

// ===== Combined Store Type =====
export type ConversationsStore = ConversationsState & ConversationsActions;

// ===== Initial State =====
const initialState: ConversationsState = {
  conversations: [],
  activeConversationId: null,
  isLoading: false,
  isCreating: false,
  isDeleting: {},
  error: null,
  currentPage: 1,
  hasNextPage: true,
  isFetchingNextPage: false,
  totalConversations: 0,
};

// ===== Store Implementation =====
export const useConversationsStore = create<ConversationsStore>()(
  (set, get) => ({
    ...initialState,

    // ===== Data Management =====
    setConversations: (conversations: Conversation[]) =>
      set({ conversations, error: null }),

    addConversations: (newConversations: Conversation[]) =>
      set((state) => ({
        conversations: [...state.conversations, ...newConversations],
        error: null,
      })),

    addConversation: (conversation: Conversation) =>
      set((state) => ({
        conversations: [conversation, ...state.conversations],
        totalConversations: state.totalConversations + 1,
        error: null,
      })),

    updateConversation: (id: string, updates: Partial<Conversation>) =>
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv._id === id ? { ...conv, ...updates } : conv
        ),
        error: null,
      })),

    removeConversation: (id: string) =>
      set((state) => ({
        conversations: state.conversations.filter((conv) => conv._id !== id),
        activeConversationId:
          state.activeConversationId === id ? null : state.activeConversationId,
        totalConversations: Math.max(0, state.totalConversations - 1),
        isDeleting: { ...state.isDeleting, [id]: false },
        error: null,
      })),

    // ===== Active Chat Management =====
    setActiveConversation: (id: string | null) =>
      set({ activeConversationId: id }),

    // ===== Loading States =====
    setLoading: (loading: boolean) => set({ isLoading: loading }),

    setCreating: (creating: boolean) => set({ isCreating: creating }),

    setDeleting: (id: string, deleting: boolean) =>
      set((state) => ({
        isDeleting: { ...state.isDeleting, [id]: deleting },
      })),

    setError: (error: Error | null) => set({ error }),

    // ===== Pagination =====
    setCurrentPage: (page: number) => set({ currentPage: page }),

    setHasNextPage: (hasNext: boolean) => set({ hasNextPage: hasNext }),

    setFetchingNextPage: (fetching: boolean) =>
      set({ isFetchingNextPage: fetching }),

    setTotalConversations: (total: number) =>
      set({ totalConversations: total }),

    // ===== Utilities =====
    reset: () => set(initialState),

    getConversationById: (id: string) =>
      get().conversations.find((conv) => conv._id === id),
  })
);

export default useConversationsStore;
